import type { VehicleListMapsResponse } from '@gm-commercial/rtk-clients/assets/types';
import type { VehicleMapMarkerStatus } from '~/common/models/asset.model';
import { GearStatus } from '~/common/models/asset.model';
import type { SearchCriteria } from './common.model';

export enum FleetMapSessionViewType {
  FLEET_MAP_LIST = 'fleetMapList',
  FLEET_MAP_FILTERS = 'fleetMapFilters',
  VEHICLE_INTERACTION = 'vehicleInteraction',
}

export enum FleetMapFilterType {
  STATUS = 'vehicleStatus',
  DIAGNOSTIC = 'diagnostic',
  FUEL_OR_CHARGE = 'fuelChargeLevel',
}

export enum VehicleStatus {
  MOVING = 'MOVING',
  PARKED = 'PARK',
  IDLING = 'IDLING',
  OFFLINE = 'OFFLINE',
  LOCATION_MASKED = 'LOCATION_MASKED',
  UNKNOWN = 'UNKNOWN',
}

export enum DiagnosticState {
  CRITICAL = 'CRITICAL',
  WARNING = 'WARNING',
  OK = 'OK',
}

export enum FuelChargeLevel {
  TWENTY_PERCENT_OR_LESS = 'LOW',
  TWENTY_ONE_TO_FIFTY = 'LOW_TO_MID',
  FIFTY_ONE_TO_EIGHTY = 'MID_TO_HIGH',
  EIGHTY_ONE_OR_MORE = 'HIGH',
}

export interface MapFilterCriteria {
  [FleetMapFilterType.STATUS]: VehicleStatus[];
  [FleetMapFilterType.DIAGNOSTIC]: DiagnosticState[];
  [FleetMapFilterType.FUEL_OR_CHARGE]: FuelChargeLevel[];
}

export interface FleetMapList {
  // TODO: add any state we want to store here
  [key: string]: unknown;
}

export interface FleetMapFilter {
  searchCriteria: SearchCriteria;
  filterType: MapFilterCriteria;
}

export interface VehicleInteractionState {
  selectedVehicle: VehicleLocation | null;
  hoveredVehicle: VehicleLocation | null;
  vehicleDetails: VehicleListMapsResponse | null;
  centerOnFleet?: boolean;
}

export enum FleetMapListViewMode {
  VEHICLES = 'vehicleList',
  GEOFENCES = 'geofencesList',
}

export interface FleetMapState {
  [FleetMapSessionViewType.FLEET_MAP_LIST]: Partial<FleetMapList>;
  [FleetMapSessionViewType.FLEET_MAP_FILTERS]: FleetMapFilter;
  [FleetMapSessionViewType.VEHICLE_INTERACTION]: VehicleInteractionState;
}

export interface FleetMapVehicle {
  vehicleName: string;
  vin: string;
  make: string;
  model: string;
  year: number;
  imageUrl?: string; // Vehicle image URL
  vehicleType: 'EV' | 'ICE'; // Electric Vehicle or Internal Combustion Engine
  batteryLevel?: number; // For EV vehicles (percentage)
  fuelLevel?: number; // For ICE vehicles (percentage)
  diagnosticState: DiagnosticState;
  vehicleStatus: VehicleStatus;
  location: VehicleLocation;
}

export interface VehicleLocation {
  id: string;
  longitude: number;
  latitude: number;
  status?: VehicleMapMarkerStatus;
  direction?: number; // Direction in degrees
}

export interface ClusterLocation {
  longitude: number;
  latitude: number;
  count: number;
}

export interface VehicleLocationMapContentProps {
  userBounds?: GeoJSON.BBox;
  onBoundsUpdate?: (bounds: GeoJSON.BBox) => void; // Callback to handle bounds updates from the child component
}

// Type for the vehicle metadata array structure
// Can be either [gearStatus] or [gearStatus, direction]
export type VehicleMetadataArray =
  | [VehicleMapMarkerStatus]
  | [VehicleMapMarkerStatus, number];
